<main class="platform-home" aria-labelledby="platform-home-title">
  <ui-section>
    <ui-block width="fill">
      <section class="platform-home__command-deck" aria-describedby="platform-home-subtitle">
        <div class="platform-home__command-main">
          <p class="platform-home__eyebrow">Protected admin dashboard</p>

          <div class="platform-home__heading-row">
            <div class="platform-home__heading-copy">
              <h1 id="platform-home-title" class="platform-home__title">Platform operations center</h1>
              <p id="platform-home-subtitle" class="platform-home__subtitle">
                {{ dashboardIntro() }}
              </p>
            </div>

            <ul class="platform-home__status-tags" aria-label="Dashboard status tags">
              <li
                *ngFor="let tag of dashboardStatusTags()"
                [ngClass]="['platform-home__status-tag', 'platform-home__status-tag--' + tag.tone]"
              >
                {{ tag.label }}
              </li>
            </ul>
          </div>

          <div class="platform-home__summary-grid" role="list" aria-label="Dashboard summary">
            <article
              *ngFor="let card of dashboardSummaryCards()"
              [ngClass]="['platform-home__summary-card', 'platform-home__summary-card--' + card.kind]"
              role="listitem"
            >
              <p class="platform-home__summary-label">{{ card.label }}</p>
              <p class="platform-home__summary-value">{{ card.value }}</p>
              <p class="platform-home__summary-detail">{{ card.detail }}</p>
            </article>
          </div>

          <div class="platform-home__guardrails">
            <p class="platform-home__section-kicker">Dashboard guardrails</p>
            <ul class="platform-home__guardrail-list">
              <li *ngFor="let guardrail of dashboardGuardrails">{{ guardrail }}</li>
            </ul>
          </div>
        </div>

        <aside class="platform-home__session-card" aria-label="Current session overview">
          <p class="platform-home__section-kicker">Current session</p>
          <h2 class="platform-home__session-title">{{ actorDisplayName() }}</h2>
          <p class="platform-home__session-stance">{{ dashboardStance() }}</p>

          <dl class="platform-home__definition-list">
            <div>
              <dt>Provider</dt>
              <dd>{{ actorProviderLabel() }}</dd>
            </div>
            <div>
              <dt>Actor ID</dt>
              <dd>{{ actorIdLabel() }}</dd>
            </div>
            <div>
              <dt>User ID</dt>
              <dd>{{ actorUserIdLabel() }}</dd>
            </div>
          </dl>

          <div class="platform-home__session-group">
            <p class="platform-home__panel-label">Resolved roles</p>
            <ul class="platform-home__badge-list" aria-label="Resolved roles">
              <li *ngFor="let role of roleBadges()">{{ role }}</li>
              <li *ngIf="roleBadges().length === 0" class="platform-home__badge-list-empty">No roles returned</li>
            </ul>
          </div>

          <div class="platform-home__session-group">
            <p class="platform-home__panel-label">Resolved scopes</p>
            <ul class="platform-home__badge-list platform-home__badge-list--stacked" aria-label="Resolved scopes">
              <li *ngFor="let scope of scopeSummaries()">
                <strong>{{ scope.label }}</strong>
                <span>{{ scope.detail }}</span>
              </li>
              <li *ngIf="scopeSummaries().length === 0" class="platform-home__badge-list-empty">No scope assignments returned</li>
            </ul>
          </div>
        </aside>
      </section>
    </ui-block>
  </ui-section>

  <ui-section>
    <ui-block width="fill">
      <div class="platform-home__dashboard-grid">
        <div class="platform-home__panel-shell platform-home__panel-shell--span-4">
          <ui-panel panelTitle="Quick actions" title="Quick actions">
            <div class="platform-home__panel-copy">
              <p class="platform-home__section-intro">
                Only protected routes supported by the current-user access profile are advertised here. Unsupported actions remain hidden until the profile
                changes.
              </p>

              <ul class="platform-home__quick-links" aria-label="Protected quick actions">
                <li *ngFor="let entryPoint of visibleEntryPoints()">
                  <a [routerLink]="entryPoint.route" class="platform-home__quick-link-card">
                    <span class="platform-home__quick-link-label">{{ entryPoint.label }}</span>
                    <span class="platform-home__quick-link-description">{{ entryPoint.description }}</span>
                  </a>
                </li>
              </ul>
            </div>
          </ui-panel>
        </div>

        <div class="platform-home__panel-shell platform-home__panel-shell--span-8">
          <ui-panel panelTitle="Priority queues" title="Priority queues">
            <div class="platform-home__panel-copy">
              <p class="platform-home__section-intro">
                These cards use the implemented workflow worklist and scheduling projection endpoints when the current session grants workflow runtime read
                access.
              </p>

              <div class="platform-home__queue-grid" role="list" aria-label="Priority queues">
                <article class="platform-home__queue-card" role="listitem" *ngFor="let queue of dashboardQueues()">
                  <div class="platform-home__card-topline">
                    <span
                      [ngClass]="['platform-home__source-pill', 'platform-home__source-pill--' + queue.sourceTone]"
                    >
                      {{ queue.source }}
                    </span>
                    <p class="platform-home__card-audience">{{ queue.audience }}</p>
                  </div>

                  <h3 class="platform-home__card-title">{{ queue.title }}</h3>
                  <p class="platform-home__card-description">{{ queue.description }}</p>

                  <ul class="platform-home__queue-items">
                    <li *ngFor="let item of queue.items">{{ item }}</li>
                  </ul>
                </article>
              </div>
            </div>
          </ui-panel>
        </div>

        <div class="platform-home__panel-shell platform-home__panel-shell--span-7">
          <ui-panel panelTitle="Capability spotlights" title="Capability spotlights">
            <div class="platform-home__panel-copy">
              <p class="platform-home__section-intro">
                Spotlights below are live access-backed modules. They appear only when the resolved current-user access profile supports them.
              </p>

              <div *ngIf="visibleCapabilities().length > 0; else noCapabilities" class="platform-home__capability-grid" role="list">
                <article class="platform-home__capability-card" role="listitem" *ngFor="let capability of visibleCapabilities()">
                  <div class="platform-home__card-topline">
                    <span class="platform-home__source-pill platform-home__source-pill--live">Live access profile</span>
                    <p class="platform-home__card-audience">{{ capability.audience }}</p>
                  </div>

                  <h3 class="platform-home__card-title">{{ capability.title }}</h3>
                  <p class="platform-home__card-description">{{ capability.description }}</p>

                  <ul class="platform-home__capability-list">
                    <li *ngFor="let outcome of capability.outcomes">{{ outcome }}</li>
                  </ul>

                  <a *ngIf="capability.entry" [routerLink]="capability.entry.route" class="platform-home__text-link">
                    {{ capability.entry.label }}
                  </a>
                  <p *ngIf="capability.entry" class="platform-home__card-help">{{ capability.entry.description }}</p>
                </article>
              </div>

              <ng-template #noCapabilities>
                <div class="platform-home__empty-state">
                  <h3>No access-backed spotlights are currently mapped for this session.</h3>
                  <p>
                    The access profile resolved successfully, but the protected home intentionally fails closed until more modules are explicitly modeled for the
                    returned access set.
                  </p>
                </div>
              </ng-template>
            </div>
          </ui-panel>
        </div>

        <div class="platform-home__panel-shell platform-home__panel-shell--span-5">
          <ui-panel panelTitle="Operational watchlist" title="Operational watchlist">
            <div class="platform-home__panel-copy">
              <p class="platform-home__section-intro">
                Live access posture and endpoint availability stay visible together so operators can distinguish authorization from data availability.
              </p>

              <ul class="platform-home__alert-list" aria-label="Operational watchlist">
                <li
                  *ngFor="let alert of dashboardAlerts()"
                  [ngClass]="['platform-home__alert-card', 'platform-home__alert-card--' + alert.severity]"
                >
                  <div class="platform-home__card-topline">
                    <span
                      [ngClass]="['platform-home__source-pill', 'platform-home__source-pill--' + alert.sourceTone]"
                    >
                      {{ alert.source }}
                    </span>
                    <span class="platform-home__alert-severity">{{ alert.severity }}</span>
                  </div>

                  <h3 class="platform-home__card-title">{{ alert.title }}</h3>
                  <p class="platform-home__card-description">{{ alert.detail }}</p>
                </li>
              </ul>
            </div>
          </ui-panel>
        </div>

        <div class="platform-home__panel-shell platform-home__panel-shell--span-7">
          <ui-panel panelTitle="Recent pilot activity" title="Recent pilot activity">
            <div class="platform-home__panel-copy">
              <p class="platform-home__section-intro">
                Activity items combine live session facts with the current workflow and scheduling projection load state.
              </p>

              <ol class="platform-home__activity-feed" aria-label="Recent pilot activity">
                <li class="platform-home__activity-item" *ngFor="let activity of dashboardActivityFeed()">
                  <div class="platform-home__card-topline">
                    <span
                      [ngClass]="['platform-home__source-pill', 'platform-home__source-pill--' + activity.sourceTone]"
                    >
                      {{ activity.source }}
                    </span>
                  </div>

                  <h3 class="platform-home__card-title">{{ activity.title }}</h3>
                  <p class="platform-home__card-description">{{ activity.detail }}</p>
                  <p class="platform-home__activity-meta">{{ activity.meta }}</p>
                </li>
              </ol>
            </div>
          </ui-panel>
        </div>

        <div class="platform-home__panel-shell platform-home__panel-shell--span-5">
          <ui-panel panelTitle="JMS as a pilot proof point" title="JMS as a pilot proof point">
            <div class="platform-home__panel-copy">
              <p class="platform-home__section-intro">
                JMS remains a bounded pilot proof point inside the platform dashboard. It validates shared foundations without becoming the platform brand.
              </p>

              <ul class="platform-home__bullet-list">
                <li *ngFor="let proofPoint of pilotProofPoints">{{ proofPoint }}</li>
              </ul>
            </div>
          </ui-panel>
        </div>

        <div class="platform-home__panel-shell platform-home__panel-shell--full">
          <ui-panel panelTitle="Ready for extension" title="Ready for extension">
            <div class="platform-home__panel-copy platform-home__panel-copy--split">
              <div>
                <p class="platform-home__section-intro">
                  This protected home now reads the implemented platform runtime surfaces while keeping later client or domain modules behind the same
                  access-profile contract.
                </p>

                <ol class="platform-home__ordered-list">
                  <li *ngFor="let path of extensionPaths">{{ path }}</li>
                </ol>
              </div>

              <div>
                <p class="platform-home__panel-label">Implementation boundaries</p>
                <ul class="platform-home__bullet-list">
                  <li *ngFor="let guardrail of dashboardGuardrails">{{ guardrail }}</li>
                </ul>
              </div>
            </div>
          </ui-panel>
        </div>
      </div>
    </ui-block>
  </ui-section>
</main>
